
/**
 * @fileOverview ExpandContentView module definition
 *
 * @author a4qtszz
 * @version 1.0
 * @modified 11/11/2014
 *
 */

(function (context) {
    'use strict';
    var require = context.require;
    var requirejs = context.requirejs;
    var define = context.define;

    define('views/ExpandContentView',['require','jquery','lib/Util','GlobalEventDispatcher','GlobalResizeListener'],function (require) {
        var $ = require('jquery');
        var Util = require('lib/Util');
        var GlobalEventDispatcher = require('GlobalEventDispatcher');
        var GlobalResizeListener = require('GlobalResizeListener');

        /**
         * This slides down content containing a sub item, much like the accordion but without many of the
         * behaviors
         *
         * @class ExpandContentView
         * @param {jQuery} $element A reference to the containing DOM element
         * @constructor
         */
        var ExpandContentView = function ($element) {
            /**
             * Variables Containing Classes for easy changing
             *
             * @default null
             * @property EXPAND_TRIGGER, EXPAND_TARGET
             * @type string
             */

            this.EXPAND_TRIGGER = '.js-expandableContent-trigger';
            this.EXPAND_TARGET = '.js-expandableContent-target';

            /**
             * A reference to the containing DOM element
             *
             * @default null
             * @property $element
             * @type {jQuery}
             */
            this.$element = $element;

            /**
             * The global instance of an event dispatcher, used to subscribe to or publish events of global interest
             *
             * @default GlobalEventDispatcher.getEventDispatcher()
             * @property GlobalEventDispatcher
             * @type {jQuery}
             */
            this.eventDispatcher = GlobalEventDispatcher.getEventDispatcher();

            /**
             * The global instance of an event dispatcher, used to subscribe to or publish events of global interest
             *
             * @default GlobalResizeListener.getResizeListener()
             * @property GlobalResizeListener
             * @type {jQuery}
             */
            this.globalResizeListener = GlobalResizeListener.getResizeListener();

            /**
             * Tracks whether component is enabled.
             *
             * @default false
             * @name isEnabled
             * @type bool
             */
            this.isEnabled = false;

            /**
             * Tracks whether component is in small context.
             *
             * @default false
             * @name isEnabled
             * @type bool
             */
            this.isSmallContext = false;
            this.settings = {};
            this.init();
        };

        /**
         * Initializes the UI Component View
         * Runs a single setupHandlers call, followed by createChildren and layout
         * Exits early if it is already initialized
         *
         * @method init
         * @returns {ExpandContentView}
         * @private
         */
        ExpandContentView.prototype.init = function () {

            this.setOptions()
                .setupHandlers()
                .createChildren()
                .resizeCheck()
                .layout()
                .enable();

            return this;
        };

        /**
         * This sets the options via data-options in the html markup. If
         * the options aren't set it goes to the defaults.
         *
         * @method setOptions
         * @returns {ExpandContentView}
         * @private
         */
        ExpandContentView.prototype.setOptions = function () {
            var options = this.$element.data().options;

            var defaults = {
                goesToTop: false,
                overTheTop: 50,
                labelOpen: '',
                labelClose: ''
            };
            this.settings = $.extend({}, defaults, options);
            return this;
        };

        /**
         * Binds the scope of any handler functions
         * Should only be run on initialization of the view
         *
         * This creates event handlers and binds the scope
         * of the JS.
         *
         * @method setupHandlers
         * @returns {ExpandContentView}
         * @private
         */
        ExpandContentView.prototype.setupHandlers = function () {
            // Bind event handlers scope here
            this.onClickHandler = this.onClick.bind(this);
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.WINDOW_RESIZE, this.layout.bind(this));
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.CONTEXT_CHANGE, this.resizeCheck.bind(this));


            return this;
        };

        /**
         * Create any child objects or references to DOM elements
         * Should only be run on initialization of the view
         *
         * @method createChildren
         * @returns {ExpandContentView}
         * @private
         */
        ExpandContentView.prototype.createChildren = function () {
            this.$expandTrigger = this.$element.find(this.EXPAND_TRIGGER);
            this.$expandTarget = this.$element.find(this.EXPAND_TARGET);

            return this;
        };

        /**
         * Remove any child objects or references to DOM elements
         *
         * @method removeChildren
         * @returns {ExpandContentView}
         * @public
         */
        ExpandContentView.prototype.removeChildren = function () {
            this.$expandTrigger = null;
            this.$expandTarget = null;

            return this;
        };

        /**
         * Performs measurements and applys any positioning style logic
         * Should be run anytime the parent layout changes
         *
         * @method layout
         * @returns {ExpandContentView}
         * @private
         */
        ExpandContentView.prototype.layout = function() {
            this.$expandTarget.addClass('isInactive');
            return this;
        };

        /**
         * Enables the component
         * Performs any event binding to handlers
         * Exits early if it is already enabled
         *
         * @method enable
         * @returns {ExpandContentView}
         * @public
         */
        ExpandContentView.prototype.enable = function () {
            if (this.isEnabled) {
                return this;
            }
            this.isEnabled = true;

            this.$expandTrigger.on('click', 'a', this.onClickHandler);
            return this;
        };

        /**
         * Disables the component
         * Tears down any event binding to handlers
         * Exits early if it is already disabled
         *
         * @method disable
         * @returns {ExpandContentView}
         * @public
         */
        ExpandContentView.prototype.disable = function () {
            if (!this.isEnabled) {
                return this;
            }
            this.isEnabled = false;

            return this;
        };

        /**
         * Destroys the component
         * Tears down any events, handlers, elements
         * Should be called when the object should be left unused
         *
         * @method destroy
         * @returns {ExpandContentView}
         * @public
         */
        ExpandContentView.prototype.destroy = function () {
            this.disable()
                .removeChildren();
            return this;
        };

        //////////////////////////////////////////////////////////////////////////////////
        // EVENT HANDLERS
        //
        // Place events here, use event handlers to access it
        //////////////////////////////////////////////////////////////////////////////////

        /**
         * Clicking on the trigger for this method causes the method to check if the 'isActive'
         * class is on the trigger. If so it removes the 'isActive' class, slides up the target
         * panel and then adds the 'isInactive' to the panel. If it is in small context it checks
         * to see if goesToTop is true and then jumps the view to the top of the trigger. If the
         * 'isActive' class is not on the trigger, it will add the 'isActive' class to the trigger.
         * Then it will slide down the target and remove the 'isInactive' class from the target.
         *
         * @method onClick
         * @returns {ExpandContentView}
         * @param {Event} e JavaScript event object
         * @private
         */

        ExpandContentView.prototype.onClick = function (e) {
            if (e.preventDefault) {
                e.preventDefault();
            }
            else {
                //needed for IE Support
                e.returnValue = false;
            }
            var self = this;
            if(this.$expandTrigger.hasClass('MMM--isActive')) {
                 
                // Item is already open
                this.$expandTrigger.removeClass('MMM--isActive').find('.js-triggerLink');
                this.$expandTrigger.html('<a href="#content1" class="js-triggerLink MMM--basicLink MMM--txt_bold"  target="_self" ><i class="MMM--icn MMM--icn_arrowDown mix-MMM--icn_med mix-MMM--icn_dark"></i> <span class="js-expandableContent-text">'+self.settings.labelOpen+'</span></a>');
                this.$expandTrigger.find('.js-triggerLink').attr({'role':'button','aria-expanded':'false'});
                // If the target contains an inline video or rG-63 player, we need to pause it
                if(typeof videoMods !== 'undefined') {
                    $debug('Found inline video module array...', 'ExpandContentView');

                    for(var i=0, l=videoMods.length; i<l; i++) {
                        $debug('Checking to see if video with video ID "' + videoMods[i].videoID + '" is contained in responsive accordion target...', 'ExpandContentView');

                        if($.contains(this.$expandTarget[0], videoMods[i].$parentComponent[0])) {
                            $debug('Responsive accordion target does contain inline video player video ID "' + videoMods[i].videoID + '", unloading player...', 'ExpandContentView');

                            videoMods[i].unloadPlayer();
                        }
                    }
                  
                }

                this.$expandTarget.slideUp(function() {
                    $(this).addClass('isInactive');

                    // This checks to see if the window is in smallContext and that goesToTop
                    // is set to true. On close the window jumps to the top of the trigger
                    // or above, based on the overTheTop set in the markup so this is configurable
                    // on the markup side.
                    // this.$expandTrigger.unbind();
                    if (self.isSmallContext && self.settings.goesToTop) {
                        $(window).scrollTop((self.$expandTrigger.offset().top) - self.settings.overTheTop);
                    }
                });
                
            } else {
                // Item is already closed
                this.$expandTrigger.addClass('MMM--isActive').find('.js-triggerLink');
                this.$expandTarget.slideDown(function() {
                    $(this).removeClass('isInactive');
                   
                });
                 this.$expandTrigger.html('<a href="#content1" class="js-triggerLink MMM--basicLink MMM--txt_bold"  target="_self" ><i class="MMM--icn MMM--icn_arrowUp mix-MMM--icn_med mix-MMM--icn_dark"></i> <span class="js-expandableContent-text">'+self.settings.labelClose+'</span></a>');
                 this.$expandTrigger.find('.js-triggerLink').attr({'role':'button','tabindex':'0', 'aria-expanded':'true'});
                 this.$expandTrigger.find('.js-triggerLink').focus();
            }

            
            this.eventDispatcher.publish('Container State Change');
            this.$expandTrigger.find('.js-triggerLink').focus();
        };

        /**
         * This checks the current context from the event dispatcher. If it is
         * in small context, isSmallContext sets to true, any other context sets to
         * false.
         *
         * @method resizeCheck
         * @returns {ExpandContentView}
         * @private
         */

        ExpandContentView.prototype.resizeCheck = function() {
            var context = this.globalResizeListener.getCurrentContext();

            if(context === 'smallContext') {
                this.isSmallContext = true;
            } else {
                this.isSmallContext = false;
            }

            return this;

        };

        return ExpandContentView;
    });

}(MMMRequire));




